import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import useNotificationPreferences from 'features/myVaro/hooks/notifications/useNotificationPreferences';
import React, { useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, StyleSheet, View } from 'react-native';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import Snackbar from 'shared/components/vxl/Snackbar/Snackbar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isAndroid, isIOS } from 'shared/constants/platform';
import SectionedContentScreen from 'shared/containers/vxl/SectionedContentScreen/SectionedContentScreen';
import ErrorScreen from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

const NotificationSettingsScreen = () => {
  const showBottomSheet = useCallback(() => {
    bottomSheetRef?.current?.present();
  }, []);

  const hideBottomSheet = useCallback(() => {
    bottomSheetRef?.current?.close();
  }, []);

  const { response, loading, errorModel, showSnackbar } =
    useNotificationPreferences(showBottomSheet);

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'notifications',
  });

  const bottomSheetRef = useRef<BottomSheetModal>(null);

  const handleNotRightNowButton = () => {
    hideBottomSheet();
  };

  const handleGoToSystemSettingsButton = () => {
    if (isIOS) {
      Linking.openURL('app-settings:').catch((err) =>
        console.error('Failed to open settings', err),
      );
    } else if (isAndroid) {
      Linking.openSettings().catch((err) =>
        console.error('Failed to open settings', err),
      );
    }
    hideBottomSheet();
  };

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          title: translatedText('notificationSettings.title'),
          headerShown: true,
          headerBackVisible: true,
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      {showSnackbar ? (
        <Snackbar
          text={translatedText('notificationSettings.changesNotSavedMessage')}
          duration={4000}
        />
      ) : null}

      <BottomSheet
        contentContainerStyle={styles.bottomSheet}
        ref={bottomSheetRef}
      >
        <View style={styles.textContainer}>
          <TitleMdText>{translatedText('bottomSheet.title')}</TitleMdText>
          <BodyBaseText>{translatedText('bottomSheet.subtitle')}</BodyBaseText>
        </View>
        <View style={styles.buttonContainer}>
          <Button
            label={translatedText('bottomSheet.buttons.goToSystemSettings')}
            onPress={handleGoToSystemSettingsButton}
            size="LARGE"
            type="PRIMARY"
            width="FULL"
          />
          <Button
            label={translatedText('bottomSheet.buttons.notRightNow')}
            onPress={handleNotRightNowButton}
            size="LARGE"
            type="SECONDARY"
            width="FULL"
          />
        </View>
      </BottomSheet>

      <SectionedContentScreen
        initialNumToRender={25}
        model={response?.sectionList ?? { sections: [] }}
      />
      <ScreenTransitionOverlay
        isLoading={loading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={errorModel ? <ErrorScreen {...errorModel} /> : null}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  bottomSheet: { padding: theme.spacing.m },
  buttonContainer: { gap: theme.spacing.m, paddingVertical: theme.spacing.m },
  container: {
    backgroundColor: theme.color.background.primary,
    flexGrow: 1,
  },
  textContainer: { gap: theme.spacing.s, paddingVertical: theme.spacing.m },
});

export default NotificationSettingsScreen;
